import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, imgUrl } from "./api";

const Ctx = createContext({ settings: null, reload: () => {} });

export const DEFAULT_THEME = {
  colors: {
    ink: "#2C2016", accent: "#C4823F", cta: "#B8532F", forest: "#264639", paper: "#FBF8F2", paper2: "#F3EFE6",
    muted: "#4A3525", body: "#3B2C1F", gold: "#E8C9A0", brown: "#8A5A2B", deep: "#1A130C",
  },
  font_display: "Cormorant Garamond",
  font_body: "Plus Jakarta Sans",
  hero_size: 96,
  heading_scale: 1,
  body_scale: 1,
};

const rgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" ");
};

export const applyTheme = (theme, logo) => {
  const t = { ...DEFAULT_THEME, ...(theme || {}), colors: { ...DEFAULT_THEME.colors, ...(theme?.colors || {}) } };
  const root = document.documentElement.style;
  Object.entries(t.colors).forEach(([k, v]) => { const c = rgb(v); if (c) root.setProperty(`--c-${k}`, c); });
  root.setProperty("--font-display", `"${t.font_display}"`);
  root.setProperty("--font-body", `"${t.font_body}"`);
  root.setProperty("--heading-scale", String(t.heading_scale || 1));
  root.setProperty("--body-scale", String(t.body_scale || 1));
  const fams = [t.font_display, t.font_body].filter((f) => f && !["Cormorant Garamond", "Plus Jakarta Sans"].includes(f));
  let link = document.getElementById("theme-fonts");
  if (fams.length) {
    if (!link) { link = document.createElement("link"); link.id = "theme-fonts"; link.rel = "stylesheet"; document.head.appendChild(link); }
    link.href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${encodeURIComponent(f)}:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400`).join("&")}&display=swap`;
  }
  if (logo?.url) {
    const href = `${imgUrl(logo.url)}?v=${encodeURIComponent(logo.url.slice(-12))}`;
    document.querySelectorAll("link[rel~='icon'], link[rel='apple-touch-icon']").forEach((n) => { if (n.getAttribute("href") !== href) n.remove(); });
    if (!document.querySelector(`link[rel='icon'][href='${href}']`)) {
      ["icon", "apple-touch-icon"].forEach((rel) => {
        const l = document.createElement("link");
        l.rel = rel;
        l.href = href;
        document.head.appendChild(l);
      });
    }
  }
  const tc = t.hero_text_color && rgb(t.hero_text_color);
  if (tc) root.setProperty("--c-herotext", tc);
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const reload = useCallback(() => {
    api.get("/settings", { params: { _: Date.now() } }).then((r) => setSettings(r.data || {})).catch(() => setSettings((s) => s || {}));
  }, []);
  useEffect(() => {
    reload();
    const onVis = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reload]);
  useEffect(() => {
    if (settings) applyTheme(settings.theme, settings.logo);
  }, [settings]);
  return <Ctx.Provider value={{ settings, reload }}>{children}</Ctx.Provider>;
};

export const useSettings = () => useContext(Ctx).settings || {};
export const useReloadSettings = () => useContext(Ctx).reload;

export const heroText = (settings, lang, key) => {
  const ht = settings?.hero_text || {};
  return (ht[lang]?.[key] || ht.es?.[key] || ht.en?.[key] || Object.values(ht).map((v) => v?.[key]).find(Boolean) || "").trim();
};
